
import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faTrash,
  faChevronDown,
  faChevronUp,
  faListOl,
  faPhone,
  faPlayCircle
} from '@fortawesome/free-solid-svg-icons';

import AudioSelectorUploads from '../../Audio/AudioSelectorUploads';
import TimeConditionManager from '../../shared/TimeConditionManager';
import StasisFallbackInputs from '../../shared/StasisFallbackInputs';
import * as URAApi from '../../../../services/uraApi';
import '../URAComponents.css';
import { isDestinationContext } from '../../../../services/destinationContexts';
import { integerInputValue, integerOrDefault, parseIntegerInput } from '../../../../services/integerInput';

function ExtensionManager({ extensions, onChange, tenant, hasWhatsAppIntegration = false, availableQueues = [], availableContexts = [], availableEndpoints = [], userRole = '' }) {
  
  const [expandedExtensions, setExpandedExtensions] = useState([0]);
  const [availableTrunks, setAvailableTrunks] = useState([]);
  const [loadingTrunks, setLoadingTrunks] = useState(false);
  
  console.log('📋 [EXT-MANAGER] Filas disponíveis:', availableQueues.length);
  console.log('📋 [EXT-MANAGER] Contextos disponíveis:', availableContexts.length);
  
  useEffect(() => {
    if (tenant) {
      loadAvailableTrunks();
    }
  }, [tenant]);
  
  const loadAvailableTrunks = async () => {
    setLoadingTrunks(true);
    try {
      const response = await URAApi.getAvailableTrunks(tenant);
      if (response.success && response.data) {
        const filteredTrunks = response.data.filter(trunk => 
          trunk.accountcode === tenant || trunk.tenant === tenant
        );
        setAvailableTrunks(filteredTrunks);
        console.log(`✅ [EXT-MANAGER] Troncos carregados: ${filteredTrunks.length} de ${response.data.length} (filtrados por tenant: ${tenant})`);
      }
    } catch (error) {
      console.error('❌ [EXT-MANAGER] Erro ao carregar troncos:', error);
    } finally {
      setLoadingTrunks(false);
    }
  };
  
  const destinationTypes = [
    { value: 'endpoint', label: 'Ramal', description: 'Encaminhar para ramal' },
    { value: 'queue', label: 'Fila', description: 'Encaminhar para fila' },
    { value: 'external', label: 'Externo', description: 'Chamada externa' },
    { value: 'disa', label: 'DISA', description: 'DISA com contexto' },
    { value: 'whatsapp', label: 'WhatsApp', description: 'Enviar para WhatsApp', disabled: !hasWhatsAppIntegration },
    { value: 'hangup', label: 'Desligar', description: 'Encerrar chamada' },
    ...(userRole === 'root' ? [
      { value: 'stasis', label: 'Agent IA', description: 'Encaminhar para um agente.' }
    ] : [])
  ];
  
  const audioMethods = [
    { value: 'background', label: 'Background' },
    { value: 'playback', label: 'Playback' }
  ];
  
  const handleAddExtension = () => {
    const newExtensions = [...extensions, {
      extension_pattern: '',
      destination_type_main: 'endpoint',
      destination_main: '',
      audio_enabled: false,
      audio_file: '',
      audio_type: '',
      audio_accountcode: tenant,
      audio_method: 'playback',
      gotoiftime_rules: [],
      overflows: []
    }];
    onChange(newExtensions);
    const newIndex = newExtensions.length - 1;
    setExpandedExtensions([...expandedExtensions, newIndex]);
    
    setTimeout(() => {
      const newExtElement = document.querySelector(`[data-extension-index="${newIndex}"]`);
      if (newExtElement) {
        newExtElement.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
        newExtElement.style.transition = 'background-color 0.5s ease';
        newExtElement.style.backgroundColor = '#e8f4fd';
        setTimeout(() => {
          newExtElement.style.backgroundColor = '#f9f9f9';
        }, 1500);
      }
    }, 100);
  };
  
  const handleRemoveExtension = (index) => {
    const newExtensions = extensions.filter((_, i) => i !== index);
    onChange(newExtensions);
    setExpandedExtensions(expandedExtensions.filter(i => i !== index).map(i => i > index ? i - 1 : i));
  };
  
  const handleUpdateExtension = (index, field, value) => {
    const newExtensions = [...extensions];
    newExtensions[index] = {
      ...newExtensions[index],
      [field]: value
    };
    onChange(newExtensions);
  };
  
  const handleAddOverflow = (extIndex) => {
    const newExtensions = [...extensions];
    newExtensions[extIndex].overflows.push({
      timeout_seconds: 30,
      destination_type: 'endpoint',
      destination_overflow: '',
      audio_enabled: false,
      audio_file: '',
      audio_type: '',
      audio_accountcode: tenant,
      hangup_after_audio: false
    });
    onChange(newExtensions);
  };
  
  const handleRemoveOverflow = (extIndex, ovfIndex) => {
    const newExtensions = [...extensions];
    newExtensions[extIndex].overflows = newExtensions[extIndex].overflows.filter((_, i) => i !== ovfIndex);
    onChange(newExtensions);
  };
  
  const handleUpdateOverflow = (extIndex, ovfIndex, field, value) => {
    const newExtensions = [...extensions];
    newExtensions[extIndex].overflows[ovfIndex] = {
      ...newExtensions[extIndex].overflows[ovfIndex],
      [field]: value
    };
    
    if (field === 'destination_type' && ['cycle', 'whatsapp'].includes(value)) {
      newExtensions[extIndex].overflows[ovfIndex].destination_overflow = '';
    }
    
    onChange(newExtensions);
  };
  
  const toggleExpanded = (index) => {
    if (expandedExtensions.includes(index)) {
      setExpandedExtensions(expandedExtensions.filter(i => i !== index));
    } else {
      setExpandedExtensions([...expandedExtensions, index]);
    }
  };

  return (
    <div className="extension-manager-card">
      {extensions.length > 0 && (
        <button 
          type="button" 
          className="floating-add-button"
          onClick={handleAddExtension}
          title="Adicionar Nova Extensão"
        >
          <FontAwesomeIcon icon={faPlus} size="lg" />
        </button>
      )}

      <div className="extension-header">
        <h3 className="section-title">
          <FontAwesomeIcon icon={faListOl} className="section-icon" />
          Extensões da URA
        </h3>
        <button type="button" className="btn-add-extension" onClick={handleAddExtension}>
          <FontAwesomeIcon icon={faPlus} />
          Adicionar Extensão
        </button>
      </div>
      
      {extensions.length === 0 ? (
        <div className="empty-state">
          <p className="empty-state-text">Nenhuma extensão configurada. Clique em "Adicionar Extensão" para começar.</p>
        </div>
      ) : (
        extensions.map((ext, extIndex) => (
          <div 
            key={extIndex} 
            data-extension-index={extIndex}
            className="extension-item"
          >
            <div className="extension-header">
              <h4 className="extension-title">
                <FontAwesomeIcon icon={faPhone} />
                Extensão {extIndex + 1}: {ext.extension_pattern || '(não definida)'}
              </h4>
              <div className="extension-actions">
                <button
                  type="button"
                  className="btn-toggle-extension"
                  onClick={() => toggleExpanded(extIndex)}
                >
                  <FontAwesomeIcon icon={expandedExtensions.includes(extIndex) ? faChevronUp : faChevronDown} />
                </button>
                <button
                  type="button"
                  className="btn-remove-extension"
                  onClick={() => handleRemoveExtension(extIndex)}
                >
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              </div>
            </div>
            
            {expandedExtensions.includes(extIndex) && (
              <>
                <div className="field">
                  <label className="label">Padrão da Extensão *</label>
                  <div className="control">
                    <input
                      className="input"
                      type="text"
                      placeholder="Ex: 1, 10, suporte, *, #"
                      value={ext.extension_pattern || ''}
                      onChange={(e) => handleUpdateExtension(extIndex, 'extension_pattern', e.target.value)}
                      required
                    />
                  </div>
                  <p className="help">Dígito(s) que o usuário deve pressionar (ex: 1, 10, suporte, *, #)</p>
                </div>
                
                <div className="field">
                  <label className="label">Tipo de Destino</label>
                  <div className="control">
                    <div className="select is-fullwidth">
                      <select
                        value={ext.destination_type_main || 'endpoint'}
                        onChange={(e) => handleUpdateExtension(extIndex, 'destination_type_main', e.target.value)}
                      >
                        {destinationTypes.map(type => (
                          <option 
                            key={type.value} 
                            value={type.value}
                            disabled={type.disabled}
                          >
                            {type.label} {type.disabled ? '(sem integração)' : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
                
                {ext.destination_type_main === 'stasis' && (
                  <div className="field">
                    <label className="label">Agent IA</label>
                    <div className="control">
                      <input className="input" type="text" value="voice-gateway-ai (fixo)" disabled />
                    </div>
                    <StasisFallbackInputs
                      fallbackType={ext.stasis_fallback_type || ''}
                      fallbackDestination={ext.stasis_fallback_destination || ''}
                      onChange={(field, value) => handleUpdateExtension(extIndex, field, value)}
                      availableEndpoints={availableEndpoints}
                      availableQueues={availableQueues}
                      availableContexts={availableContexts}
                    />
                  </div>
                )}

                {!['hangup', 'whatsapp', 'stasis'].includes(ext.destination_type_main) && (
                  <div className="field">
                    <label className="label">Destino Principal</label>
                    <div className="control">
                      {ext.destination_type_main === 'queue' ? (
                        <div className="select is-fullwidth">
                          <select
                            value={ext.destination_main || ''}
                            onChange={(e) => handleUpdateExtension(extIndex, 'destination_main', e.target.value)}
                          >
                            <option value="">Selecione uma fila</option>
                            {availableQueues.map((queue) => (
                              <option key={queue.namequeue || queue.name} value={queue.namequeue || queue.name}>
                                {queue.namequeue || queue.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      ) : ext.destination_type_main === 'disa' ? (
                        <div className="select is-fullwidth">
                          <select
                            value={ext.destination_main || ''}
                            onChange={(e) => handleUpdateExtension(extIndex, 'destination_main', e.target.value)}
                          >
                            <option value="">Selecione um contexto</option>
                            {availableContexts.filter(context => {
                              const contextName = (context.name || context.context_name || '').toLowerCase();
                              return isDestinationContext(contextName);
                            }).map((context) => (
                              <option key={context.name || context.context_name} value={context.name || context.context_name}>
                                {context.label || context.name || context.context_name}
                              </option>
                            ))}
                          </select>
                        </div>
                      ) : ext.destination_type_main === 'external' ? (
                        <div className="field is-horizontal">
                          <div className="field-body">
                            <div className="field" style={{ flexGrow: 1, marginRight: '10px' }}>
                              <label className="label is-small">Número Externo</label>
                              <div className="control">
                                <input
                                  className="input"
                                  type="text"
                                  placeholder="Ex: 1140041234 ou 01140041234"
                                  value={ext.external_number || ''}
                                  onChange={(e) => handleUpdateExtension(extIndex, 'external_number', e.target.value)}
                                />
                              </div>
                            </div>
                            
                            <div className="field" style={{ minWidth: '200px' }}>
                              <label className="label is-small">Tronco</label>
                              <div className="control">
                                <div className={`select is-fullwidth ${loadingTrunks ? 'is-loading' : ''}`}>
                                  <select
                                    value={ext.external_trunk || ''}
                                    onChange={(e) => handleUpdateExtension(extIndex, 'external_trunk', e.target.value)}
                                  >
                                    <option value="">Selecione o tronco</option>
                                    {availableTrunks.map((trunk) => (
                                      <option key={trunk.name} value={trunk.name}>
                                        {trunk.name}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <input
                          className="input"
                          type="text"
                          placeholder={
                            ext.destination_type_main === 'endpoint' ? 'Ex: 2000' :
                            'Destino'
                          }
                          value={ext.destination_main || ''}
                          onChange={(e) => handleUpdateExtension(extIndex, 'destination_main', e.target.value)}
                        />
                      )}
                    </div>
                    {ext.destination_type_main === 'external' && (
                      <p className="help is-info" style={{ marginTop: '5px' }}>
                        💡 <strong>Dica:</strong> Insira o telefone (externo) de acordo com a discagem da operadora
                      </p>
                    )}
                    {ext.destination_type_main === 'queue' && availableQueues.length === 0 && (
                      <p className="help is-warning">Nenhuma fila encontrada. Crie filas em Configurações &gt; Filas.</p>
                    )}
                    {ext.destination_type_main === 'disa' && availableContexts.length === 0 && (
                      <p className="help is-warning">Nenhum contexto encontrado. Crie contextos em Dialplan.</p>
                    )}
                  </div>
                )}
                
                <hr />
                <div className="field">
                  <div className="control">
                    <label className="checkbox">
                      <input
                        type="checkbox"
                        checked={ext.audio_enabled || false}
                        onChange={(e) => handleUpdateExtension(extIndex, 'audio_enabled', e.target.checked)}
                      />
                      <span style={{ marginLeft: '8px' }}>Reproduzir Áudio antes de Encaminhar</span>
                    </label>
                  </div>
                </div>
                
                {ext.audio_enabled && (
                  <>
                    <AudioSelectorUploads
                      tenant={tenant}
                      selectedFile={ext.audio_file}
                      selectedType={ext.audio_type}
                      onAudioChange={(audioData) => {
                        console.log(`🎵 [EXT-MANAGER] Áudio selecionado para extensão ${extIndex}:`, audioData);
                        const newExtensions = [...extensions];
                        newExtensions[extIndex] = {
                          ...newExtensions[extIndex],
                          audio_file: audioData.file,
                          audio_type: audioData.type,
                          audio_accountcode: audioData.accountcode
                        };
                        onChange(newExtensions);
                      }}
                    />
                    <p className="help" style={{ marginTop: '-10px', marginBottom: '15px' }}>
                      <strong>*Obrigatório:</strong> Selecione um áudio para reproduzir antes de encaminhar. Se não houver áudios, faça upload em Configurações &gt; Uploads.
                    </p>
                    
                    <div className="field">
                      <label className="label">Método de Reprodução</label>
                      <div className="control">
                        <div className="select is-fullwidth">
                          <select
                            value={ext.audio_method || 'playback'}
                            onChange={(e) => handleUpdateExtension(extIndex, 'audio_method', e.target.value)}
                          >
                            {audioMethods.map(method => (
                              <option key={method.value} value={method.value}>
                                {method.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  </>
                )}
                
                <hr />
                <TimeConditionManager
                  rules={ext.gotoiftime_rules || []}
                  onChange={(rules) => handleUpdateExtension(extIndex, 'gotoiftime_rules', rules)}
                  availableContexts={availableContexts}
                  disabled={false}
                  title={`Desvios por Data/Hora da Extensão ${ext.extension_pattern || extIndex + 1}`}
                />
                
                <hr />
                <div className="level">
                  <div className="level-left">
                    <div className="level-item">
                      <h5 className="title is-6">Transbordos (Overflows)</h5>
                    </div>
                  </div>
                  <div className="level-right">
                    <div className="level-item">
                      <button
                        type="button"
                        className="button is-small is-info"
                        onClick={() => handleAddOverflow(extIndex)}
                      >
                        <FontAwesomeIcon icon={faPlus} style={{ marginRight: '6px' }} />
                        Adicionar Transbordo
                      </button>
                    </div>
                  </div>
                </div>
                
                {(!ext.overflows || ext.overflows.length === 0) ? (
                  <p className="help">Nenhum transbordo configurado.</p>
                ) : (
                  ext.overflows.map((overflow, ovfIndex) => (
                    <div key={ovfIndex} className="box" style={{ backgroundColor: '#fff', padding: '15px', marginBottom: '10px' }}>
                      <div className="level">
                        <div className="level-left">
                          <div className="level-item">
                            <strong>Transbordo {ovfIndex + 1}</strong>
                          </div>
                        </div>
                        <div className="level-right">
                          <div className="level-item">
                            <button
                              type="button"
                              className="button is-small is-danger"
                              onClick={() => handleRemoveOverflow(extIndex, ovfIndex)}
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </div>
                        </div>
                      </div>
                      
                      <div className="columns">
                        <div className="column is-4">
                          <div className="field">
                            <label className="label is-small">Timeout (segundos)</label>
                            <div className="control">
                              <input
                                className="input is-small"
                                type="number"
                                min="1"
                                max="300"
                                value={integerInputValue(overflow.timeout_seconds, 30)}
                                onChange={(e) => handleUpdateOverflow(extIndex, ovfIndex, 'timeout_seconds', parseIntegerInput(e.target.value))}
                                onBlur={(e) => handleUpdateOverflow(extIndex, ovfIndex, 'timeout_seconds', integerOrDefault(e.target.value, 30))}
                              />
                            </div>
                          </div>
                        </div>
                        
                        <div className="column is-4">
                          <div className="field">
                            <label className="label is-small">Tipo</label>
                            <div className="control">
                              <div className="select is-small is-fullwidth">
                                <select
                                  value={overflow.destination_type || 'endpoint'}
                                  onChange={(e) => handleUpdateOverflow(extIndex, ovfIndex, 'destination_type', e.target.value)}
                                >
                                  <option value="endpoint">Ramal</option>
                                  <option value="queue">Fila</option>
                                  <option value="external">Externo</option>
                                  <option value="disa">DISA</option>
                                  <option value="whatsapp" disabled={!hasWhatsAppIntegration}>WhatsApp {!hasWhatsAppIntegration ? '(sem integração)' : ''}</option>
                                  <option value="cycle">Ciclo</option>
                                  <option value="hangup">Desligar</option>
                                </select>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        <div className="column is-4">
                          {!['hangup', 'whatsapp', 'cycle'].includes(overflow.destination_type) && (
                            <div className="field">
                              <label className="label is-small">Destino</label>
                              <div className="control">
                                {overflow.destination_type === 'queue' ? (
                                  <div className="select is-fullwidth is-small">
                                    <select
                                      value={overflow.destination_overflow || ''}
                                      onChange={(e) => handleUpdateOverflow(extIndex, ovfIndex, 'destination_overflow', e.target.value)}
                                    >
                                      <option value="">Selecione uma fila</option>
                                      {availableQueues.map((queue) => (
                                        <option key={queue.namequeue || queue.name} value={queue.namequeue || queue.name}>
                                          {queue.namequeue || queue.name}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                ) : overflow.destination_type === 'disa' ? (
                                  <div className="select is-fullwidth is-small">
                                    <select
                                      value={overflow.destination_overflow || ''}
                                      onChange={(e) => handleUpdateOverflow(extIndex, ovfIndex, 'destination_overflow', e.target.value)}
                                    >
                                      <option value="">Selecione um contexto</option>
                                      {availableContexts.filter(context => {
                                        const contextName = (context.name || context.context_name || '').toLowerCase();
                                        return isDestinationContext(contextName);
                                      }).map((context) => (
                                        <option key={context.name || context.context_name} value={context.name || context.context_name}>
                                          {context.label || context.name || context.context_name}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                ) : overflow.destination_type === 'external' ? (
                                  <div className="field is-horizontal" style={{ marginBottom: '0' }}>
                                    <div className="field-body">
                                      <div className="field" style={{ flexGrow: 1, marginRight: '5px', marginBottom: '0' }}>
                                        <div className="control">
                                          <input
                                            className="input is-small"
                                            type="text"
                                            placeholder="Número (ex: 1140041234)"
                                            value={overflow.overflow_external_number || ''}
                                            onChange={(e) => handleUpdateOverflow(extIndex, ovfIndex, 'overflow_external_number', e.target.value)}
                                          />
                                        </div>
                                      </div>
                                      
                                      <div className="field" style={{ minWidth: '150px', marginBottom: '0' }}>
                                        <div className="control">
                                          <div className={`select is-small is-fullwidth ${loadingTrunks ? 'is-loading' : ''}`}>
                                            <select
                                              value={overflow.overflow_external_trunk || ''}
                                              onChange={(e) => handleUpdateOverflow(extIndex, ovfIndex, 'overflow_external_trunk', e.target.value)}
                                            >
                                              <option value="">Tronco</option>
                                              {availableTrunks.map((trunk) => (
                                                <option key={trunk.name} value={trunk.name}>
                                                  {trunk.name}
                                                </option>
                                              ))}
                                            </select>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  <input
                                    className="input is-small"
                                    type="text"
                                    placeholder={overflow.destination_type === 'endpoint' ? 'Ex: 2000' : 'Destino'}
                                    value={overflow.destination_overflow || ''}
                                    onChange={(e) => handleUpdateOverflow(extIndex, ovfIndex, 'destination_overflow', e.target.value)}
                                  />
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </>
            )}
          </div>
        ))
      )}
    </div>
  );
}

export default ExtensionManager;

